import { Injectable } from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ConfirmOptions {
  closeable?: boolean;
  customClass?: string;
  dismissable?: boolean;
  escapable?: boolean;
  supportHTMLContent?: boolean; // Boolean to determine whether to render HTML tags.
}

export interface ConfirmButtonConfig {
  id: number;
  label: string;
  class?: string;
}

export interface ConfirmResponse {
  id?: number;
  canceled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmService {
  constructor(public readonly ngxSmartModalService: NgxSmartModalService) {}

  public currentModal: Subject<ConfirmResponse>;
  private readonly hideDelay = 500; // This is only used in subscription notification, not in modal options, it's the default there!
  private buttonConfirmedId: number;
  private showLoader: boolean = false;

  private readonly confirmOptionsChanged: Subject<ConfirmOptions> = new Subject<ConfirmOptions>();
  public readonly onConfirmOptionsChanged: Observable<ConfirmOptions> = this.confirmOptionsChanged.asObservable();
  public readonly onButtonSelection: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public confirm(
    titleModal: string,
    message: string,
    confirmLabel: string,
    cancelLabel: string,
    options?: ConfirmOptions
  ): Observable<boolean> {
    const confirmButton: ConfirmButtonConfig = {
      id: 1,
      label: confirmLabel
    };
    return this.showConfirm(titleModal, message, [confirmButton], cancelLabel, options).pipe(
      map((confirmResponse: ConfirmResponse) => !confirmResponse.canceled)
    );
  }

  public confirmMultipleChoice(
    titleModal: string,
    message: string,
    confirmButtons: Array<ConfirmButtonConfig>,
    cancelLabel: string,
    options?: ConfirmOptions
  ): Observable<ConfirmResponse> {
    return this.showConfirm(titleModal, message, confirmButtons, cancelLabel, options);
  }

  private showConfirm(
    titleModal: string,
    message: string,
    confirmButtons: Array<ConfirmButtonConfig>,
    cancelLabel: string,
    options?: ConfirmOptions
  ): Observable<ConfirmResponse> {
    try {
      this.buttonConfirmedId = null;
      this.ngxSmartModalService.setModalData(
        {
          titleModal,
          message,
          labelCancel: cancelLabel,
          confirmButtons
        },
        'confirmationModal',
        true
      );

      if (options) {
        this.confirmOptionsChanged.next(options);
      }
      const modal = this.ngxSmartModalService.getModal('confirmationModal');
      modal.open();
      this.currentModal = new Subject<ConfirmResponse>();
      return this.currentModal.asObservable();
    } catch {
      return of({ canceled: true });
    }
  }

  public setResponse(buttonConfirmedId?: number): void {
    this.buttonConfirmedId = buttonConfirmedId;
    if (buttonConfirmedId && this.showLoader) {
      this.onButtonSelection.next(this.buttonConfirmedId);
      this.respond();
    } else {
      this.closeModal();
    }
  }

  public closeModal(): void {
    this.onButtonSelection.next(null);
    this.showLoader = false;
    this.ngxSmartModalService.getModal('confirmationModal').close();
  }

  public respond(): void {
    setTimeout(() => {
      this.currentModal.next({ id: this.buttonConfirmedId, canceled: !this.buttonConfirmedId });
      this.currentModal.complete();
    }, this.hideDelay);
  }

  /**
   * Set the value to true if you want to show the loader in buttons
   */
  public setShowLoader(showLoader: boolean): void {
    this.showLoader = showLoader;
  }

  public updateOptions(options: ConfirmOptions): void {
    this.confirmOptionsChanged.next(options);
  }
}
